import { SupplyId } from "../data/supplies";
import { BalanceData, Rarity } from "../data/balance";
import { EventReward, EventsData, LiveEventData } from "../data/events";
import { ChestResult } from "../sim/chest";
import { EmberSignal, SignalCounts, perOf, sourcePays } from "./embers";
import { eventForUtcDay, nextEventAfter, rotationIdMonday } from "./eventSchedule";
import { SECONDS_PER_DAY, chestOfRarity, chestShare, grantReward, metaSeed, utcDay } from "./orders";
import { PlayerProfile } from "./profile";
import { Wallet } from "./wallet";

/**
 * Offline events (FINISH_PLAN WP6, MP-21, DESIGN §10.5; balance/src/events.json): dated set-piece events and, in every
 * other week, the weekly rotation (meta/eventSchedule.ts), each with a free reward track paid in embers. What earns
 * embers is each event's own list of sources (meta/embers.ts): signals from a battle's end, Daily Orders claimed, upgrades
 * completed, Tribute collected. Embers are never spent — a node opens once the event's total reaches its threshold, and
 * each node is claimed once. Progress is keyed by the event's id (a rotation week's is `<kind>_<ISO year>w<ISO week>`),
 * so each week starts fresh. The device clock decides the day, like Daily Orders: a clock set forward reaches an event
 * early, which is accepted offline (there is no server to ask).
 */

/** Occurrence keys (run seeds, Daily Road runs) remembered so each counts once, however often its end screen is drawn. */
const KEYS_KEPT = 24;
/** Order uids remembered: more than the orders a day can hold (issued, carried and claimed). */
const ORDERS_KEPT = 48;
const DAYS_PER_WEEK = 7;
const SECONDS_PER_HOUR = 3600;

/**
 * A rotation week is pruned from the save this long after its week ended (its embers, claims and "seen" mark): a
 * weekly event would otherwise add an id a week forever. Long enough that nothing still shows it (only the live event
 * is ever read); dated events are kept (few, and their emblems are history).
 */
export const PRUNE_RECURRING_AFTER_DAYS = 8 * 7;

/** Whether `eventId` is a rotation week (of any kind, past rotations' too) whose week ended PRUNE_RECURRING_AFTER_DAYS before `now`. */
export function isStaleEvent(data: EventsData, eventId: string, now: number): boolean {
  const monday = rotationIdMonday(data, eventId);
  return monday !== null && monday + DAYS_PER_WEEK + PRUNE_RECURRING_AFTER_DAYS <= utcDay(now);
}

/** The event running on `now`'s UTC day, or null. */
export function eventAt(data: EventsData, now: number): LiveEventData | null { return eventForUtcDay(data, utcDay(now)); }

/** The next event still to start after `now`, or null. */
export function nextEvent(data: EventsData, now: number): LiveEventData | null { return nextEventAfter(data, utcDay(now)); }

export const eventStartsAt = (e: LiveEventData): number => e.startDay * SECONDS_PER_DAY;
/** The first second after the event (its last day ends at midnight UTC). */
export const eventEndsAt = (e: LiveEventData): number => e.endDay * SECONDS_PER_DAY;

export function eventsUnlocked(data: EventsData, highestCleared: number): boolean { return highestCleared >= data.unlockAfterLevel; }

/** The hub's running totals the event compares between syncs (upgrades completed, Tribute collected). */
export interface HubMarks {
  /** Tower and fortress levels summed: each completed upgrade adds one. */
  readonly levels: number;
  /** When the Tribute's current fill started (Tribute.since; −1 before it unlocks): a claim moves it. */
  readonly tributeSince: number;
  /** The Tribute's cap: the most hours one claim can hold. */
  readonly tributeCapSeconds: number;
}

export interface EventProgressJson {
  embers: Record<string, number>;
  claimed: Record<string, number[]>;
  battles: string[];
  orders: number[];
  emblems: string[];
  /** Embers per event per signal (MP-21): the sources' event-wide caps. Absent in older saves. */
  earned?: Record<string, Record<string, number>>;
  /** The hub totals last seen (MP-21): levels and Tribute's fill start. Absent until the first sync. */
  marks?: { levels?: number; tribute?: number };
}

const isCount = (n: unknown): n is number => Number.isInteger(n) && (n as number) >= 0;
const obj = (v: unknown): [string, unknown][] => (typeof v === "object" && v !== null && !Array.isArray(v) ? Object.entries(v) : []);

/** The player's embers and claims per event, the occurrences and orders already counted, the hub marks and the emblems won. */
export class EventProgress {
  private readonly embers: Record<string, number> = {};
  private readonly claimed: Record<string, number[]> = {};
  private readonly earned: Record<string, Record<string, number>> = {};
  private readonly battles: string[] = [];
  private readonly orders: number[] = [];
  private readonly emblemList: string[] = [];
  private levelsMark: number | null = null;
  private tributeMark: number | null = null;

  embersOf(eventId: string): number { return this.embers[eventId] ?? 0; }
  /** Embers the event has had from one signal (its source's cap counts against this). */
  earnedFrom(eventId: string, signal: string): number { return this.earned[eventId]?.[signal] ?? 0; }
  isClaimed(eventId: string, node: number): boolean { return (this.claimed[eventId] ?? []).includes(node); }
  get emblems(): readonly string[] { return this.emblemList; }

  private add(eventId: string, n: number): number {
    if (n <= 0) return 0;
    this.embers[eventId] = this.embersOf(eventId) + n;
    return n;
  }

  private remember(key: string): boolean {
    if (this.battles.includes(key)) return false;
    this.battles.push(key);
    if (this.battles.length > KEYS_KEPT) this.battles.splice(0, this.battles.length - KEYS_KEPT);
    return true;
  }

  /**
   * Pays `signals` by the event's sources (each capped per occurrence and across the event). With a `key` (a run's seed,
   * a Daily Road run) it counts once. Returns the embers added.
   */
  creditSignals(e: LiveEventData, key: string | null, signals: SignalCounts): number {
    if (key !== null && !this.remember(key)) return 0;
    let total = 0;
    for (const s of e.sources) {
      const pay = sourcePays(s, signals[s.signal as EmberSignal] ?? 0, perOf(e, s), this.earnedFrom(e.id, s.signal));
      if (pay <= 0) continue;
      (this.earned[e.id] ??= {})[s.signal] = this.earnedFrom(e.id, s.signal) + pay;
      total += pay;
    }
    return this.add(e.id, total);
  }

  /** Adds a fixed number of embers once per `key` (the debug row). Returns the embers added (0 when already counted). */
  creditBattle(e: LiveEventData, key: string, embers: number): number {
    return this.remember(key) ? this.add(e.id, embers) : 0;
  }

  /** Whether this run already counted (its end screen shows what it paid). */
  counted(key: string): boolean { return this.battles.includes(key); }

  /** Counts each claimed Daily Order once (by uid) as the event's `order` signal. Returns the embers added. */
  creditOrders(e: LiveEventData, claimedUids: readonly number[]): number {
    let n = 0;
    for (const uid of claimedUids) {
      if (this.orders.includes(uid)) continue;
      this.orders.push(uid);
      n++;
    }
    if (this.orders.length > ORDERS_KEPT) this.orders.splice(0, this.orders.length - ORDERS_KEPT);
    return n > 0 ? this.creditSignals(e, null, { order: n }) : 0;
  }

  /**
   * Compares the hub's totals with the last ones seen: levels gained are upgrades completed, a moved Tribute start is a
   * claim of the hours it had filled (up to the cap). Pays them to `e` (the live event, null for none: the marks still
   * move, so nothing done outside an event counts later). The first sync only sets the marks. Returns the embers added.
   */
  syncHub(e: LiveEventData | null, m: HubMarks): number {
    const upgrades = this.levelsMark === null ? 0 : Math.max(0, m.levels - this.levelsMark);
    const before = this.tributeMark;
    const hours = before !== null && before >= 0 && m.tributeSince > before ? Math.floor(Math.min(m.tributeSince - before, m.tributeCapSeconds) / SECONDS_PER_HOUR) : 0;
    this.levelsMark = Math.max(this.levelsMark ?? 0, m.levels);
    this.tributeMark = m.tributeSince;
    return e && (upgrades > 0 || hours > 0) ? this.creditSignals(e, null, { upgrade: upgrades, tribute: hours }) : 0;
  }

  /** Nodes reached and not yet claimed, in track order. */
  claimable(e: LiveEventData): number[] {
    const have = this.embersOf(e.id);
    return e.nodes.map((n, i) => (n.embers <= have && !this.isClaimed(e.id, i) ? i : -1)).filter((i) => i >= 0);
  }

  /** Marks a reached node claimed and returns its reward; null when it is not reached or already claimed (idempotent). */
  claim(e: LiveEventData, node: number): EventReward | null {
    const n = e.nodes[node];
    if (!n || n.embers > this.embersOf(e.id) || this.isClaimed(e.id, node)) return null;
    this.claimed[e.id] = [...(this.claimed[e.id] ?? []), node].sort((a, b) => a - b);
    if (n.reward.emblem && !this.emblemList.includes(n.reward.emblem)) this.emblemList.push(n.reward.emblem);
    return n.reward;
  }

  /** Drops the embers, claims and per-signal totals of events `stale` says are over for good (isStaleEvent). Returns how many were dropped. */
  prune(stale: (eventId: string) => boolean): number {
    const ids = [...new Set([...Object.keys(this.embers), ...Object.keys(this.claimed), ...Object.keys(this.earned)])].filter(stale);
    for (const id of ids) { delete this.embers[id]; delete this.claimed[id]; delete this.earned[id]; }
    return ids.length;
  }

  toJson(): EventProgressJson {
    const j: EventProgressJson = {
      embers: { ...this.embers }, claimed: Object.fromEntries(Object.entries(this.claimed).map(([k, v]) => [k, [...v]])),
      battles: [...this.battles], orders: [...this.orders], emblems: [...this.emblemList],
    };
    if (Object.keys(this.earned).length > 0) j.earned = Object.fromEntries(Object.entries(this.earned).map(([k, v]) => [k, { ...v }]));
    if (this.levelsMark !== null || this.tributeMark !== null) j.marks = { ...(this.levelsMark !== null ? { levels: this.levelsMark } : {}), ...(this.tributeMark !== null ? { tribute: this.tributeMark } : {}) };
    return j;
  }

  /** Restores field by field: a malformed entry is dropped, the rest kept (a save from before MP-21 has no `earned` or `marks`). */
  static fromJson(j: unknown): EventProgress {
    const p = new EventProgress();
    if (typeof j !== "object" || j === null) return p;
    const src = j as Partial<EventProgressJson>;
    for (const [k, v] of obj(src.embers)) if (isCount(v)) p.embers[k] = v;
    for (const [k, v] of obj(src.claimed)) if (Array.isArray(v)) p.claimed[k] = [...new Set(v.filter(isCount))].sort((a, b) => a - b);
    for (const [k, v] of obj(src.earned)) {
      const per = Object.fromEntries(obj(v).filter(([, n]) => isCount(n))) as Record<string, number>;
      if (Object.keys(per).length > 0) p.earned[k] = per;
    }
    if (Array.isArray(src.battles)) p.battles.push(...src.battles.filter((b): b is string => typeof b === "string").slice(-KEYS_KEPT));
    if (Array.isArray(src.orders)) p.orders.push(...src.orders.filter(isCount).slice(-ORDERS_KEPT));
    if (Array.isArray(src.emblems)) p.emblemList.push(...new Set(src.emblems.filter((b): b is string => typeof b === "string" && b !== "")));
    const m = src.marks;
    if (m && isCount(m.levels)) p.levelsMark = m.levels;
    if (m && Number.isInteger(m.tribute) && (m.tribute as number) >= -1) p.tributeMark = m.tribute as number;
    return p;
  }
}

/** Where a reward goes and what seeds its chest. */
export interface RewardTarget {
  readonly profile: PlayerProfile;
  readonly wallet: Wallet;
  /** The player's highest cleared level (at least 1): resources and the chest are sized for it. */
  readonly level: number;
  /** The chest's seed (metaSeed from the player and what pays it: the same chest however often a claim is retried). */
  readonly seed: bigint;
  readonly at: number;
  /** The ledger's reference ("ashen_night.5", "trial.2913"). */
  readonly ref: string;
}

/**
 * What a reward paid: gold, a speed-up, Forge Essence and Fusion Sigils into the profile, gems and Marks into the
 * wallet, and a chest to bank.
 */
export interface PaidReward {
  readonly gold: number; readonly speedUpMinutes: number; readonly gems: number; readonly marks: number;
  /** MP-23: Trophies paid into the wallet (0 for none; absent in a PaidReward built before them). */
  readonly trophies?: number;
  readonly chest: ChestResult | null; readonly emblem: string;
  readonly essence: number; readonly sigil: Rarity | null; readonly sigils: number;
  /** MP-06: the pre-battle supply it paid and how many were added (0 at the cap). */
  readonly supply?: SupplyId; readonly supplies?: number;
}

/**
 * Pays an event, Trial or Heroic reward. The chest is rolled here (the existing roll at a fixed rarity) and returned for
 * the caller to bank with its gear (app LevelCatalog.bankChest), as a star chest is.
 */
export function payReward(balance: BalanceData, reward: EventReward, t: RewardTarget): PaidReward {
  const level = Math.max(1, t.level);
  const gold = reward.resources.isZero() ? 0 : chestShare(balance, level, reward.resources);
  grantReward(t.profile, { gold, speedUpMinutes: reward.speedUpMinutes });
  t.wallet.grantGems(reward.gems, "event", t.at, t.ref);
  t.wallet.grant("marks", reward.marks, "event", t.at, t.ref);
  t.wallet.grant("trophies", reward.trophies ?? 0, "event", t.at, t.ref);
  payForge(t.profile, reward);
  const supplies = reward.supply ? t.profile.supplies.add(balance.commerce.shop.supplies, reward.supply, reward.supplies ?? 1) : 0;
  const chest = reward.chest === null ? null : chestOfRarity(balance, level, reward.chest, t.seed);
  return {
    gold, speedUpMinutes: reward.speedUpMinutes, gems: reward.gems, marks: reward.marks, trophies: reward.trophies ?? 0, chest, emblem: reward.emblem, essence: reward.essence, sigil: reward.sigil, sigils: reward.sigils,
    ...(reward.supply ? { supply: reward.supply, supplies } : {}),
  };
}

/** Pays a reward's Forge Essence and Fusion Sigils into the profile's forge (gear v2). */
export function payForge(profile: PlayerProfile, reward: Pick<EventReward, "essence" | "sigil" | "sigils">): void {
  profile.forge.essence += reward.essence;
  if (reward.sigil !== null) profile.forge.addSigils(reward.sigil, reward.sigils);
}

/** The seed of an event node's chest. */
export function eventNodeSeed(playerId: string, e: LiveEventData, node: number): bigint { return metaSeed(playerId, `event:${e.id}`, node); }
